import { useCallback, useEffect, useRef } from "react";
import ActionSheetController from "./ActionSheetController";
import type { IconPosition, InternalActionSheetButton } from "./types";

export interface UseActionSheetConfig {
  /** Header title displayed at the top of the sheet. */
  title?: string;
  /** Subtitle displayed below the title. */
  message?: string;
  /** Buttons to display. Disabled buttons (`enabled: false`) are filtered out automatically. */
  buttons: InternalActionSheetButton[];
  /**
   * Position of the icon relative to the button title.
   * @default "left"
   */
  iconPosition?: IconPosition;
  /**
   * Whether tapping the backdrop dismisses the sheet.
   * @default true
   */
  backdropDismiss?: boolean;
  /**
   * The button ID returned when the sheet is dismissed via backdrop tap.
   * @default "cancel"
   */
  cancelButtonId?: string;
  /** Called with the selected button ID after the sheet closes. */
  onSelect?: (buttonId: string) => void;
}

export interface UseActionSheetReturn {
  /** Opens the action sheet. Resolves with the selected button ID. No-ops if already showing actions. */
  show: () => Promise<string>;
  /** Dismisses the sheet (whether showing actions or loading). */
  hide: () => void;
  /**
   * Shows a loading spinner in the sheet. Call {@link show} after your async
   * work completes to transition from loading → actions with an animation.
   */
  showLoading: () => void;
}

/**
 * Hook wrapping {@link ActionSheetController} with lifecycle management.
 *
 * Returns imperative `show`, `hide`, and `showLoading` methods.
 * Wire them to your trigger however you like — `onPress`, `onClick`, or
 * any other handler. The sheet auto-hides on unmount to prevent orphaned sheets.
 *
 * For the simplest case (just a trigger element + buttons), prefer the
 * {@link ActionSheet} component instead — it wraps this hook and
 * auto-detects `onPress` vs `onClick` on the child element.
 *
 * Use this hook when you need loading states, programmatic control,
 * or a trigger that isn't a simple child element.
 *
 * @example With a React Aria Button
 * ```tsx
 * const { show } = useActionSheet({
 *   title: "File options",
 *   buttons: [
 *     { id: "edit", title: "Edit" },
 *     { id: "delete", title: "Delete", style: "destructive" },
 *   ],
 *   onSelect: (id) => console.log(id),
 * });
 *
 * return <Button onPress={() => void show()}>Options</Button>;
 * ```
 *
 * @example Loading → actions transition
 * ```tsx
 * const { showLoading, show } = useActionSheet({ buttons, onSelect });
 *
 * async function handlePress() {
 *   showLoading();
 *   const data = await fetchOptions();
 *   show(); // transitions from spinner → action buttons
 * }
 * ```
 */
export function useActionSheet(
  config: UseActionSheetConfig
): UseActionSheetReturn {
  const isShowingRef = useRef(false);
  const isLoadingRef = useRef(false);
  const configRef = useRef(config);
  configRef.current = config;

  const show = useCallback(async (): Promise<string> => {
    if (isShowingRef.current && !isLoadingRef.current) {
      return "";
    }

    const cfg = configRef.current;
    isLoadingRef.current = false;
    isShowingRef.current = true;

    const result = await ActionSheetController.showActions({
      title: cfg.title,
      message: cfg.message,
      buttons: cfg.buttons,
      iconPosition: cfg.iconPosition,
      backdropDismiss: cfg.backdropDismiss,
      cancelButtonId: cfg.cancelButtonId
    });

    isShowingRef.current = false;
    cfg.onSelect?.(result);

    return result;
  }, []);

  const hide = useCallback(() => {
    ActionSheetController.hide();
    isShowingRef.current = false;
    isLoadingRef.current = false;
  }, []);

  const showLoading = useCallback(() => {
    ActionSheetController.showLoading();
    isLoadingRef.current = true;
    isShowingRef.current = true;
  }, []);

  useEffect(() => {
    return () => {
      if (isShowingRef.current) {
        ActionSheetController.hide();
        isShowingRef.current = false;
      }
    };
  }, []);

  return { show, hide, showLoading };
}
